import { MobileRecordState } from "../layout/MobileRecordState";
import { CanAccess } from "ra-core";
import { useState } from "react";

import {
  InfiniteListBase,
  RecordRepresentation,
  ShowBase,
  useShowContext,
  useTranslate,
} from "ra-core";

import type { ShowBaseProps } from "ra-core";

import { useIsMobile } from "@/hooks/use-mobile";

import { ReferenceField } from "@/components/admin/reference-field";

import { TextField } from "@/components/admin/text-field";

import { Card, CardContent } from "@/components/ui/card";

import { SegmentedControl } from "@/components/ui/segmented-control";

import { Button } from "@/components/ui/button";

import { Separator } from "@/components/ui/separator";

import { Pencil } from "lucide-react";

import MobileHeader from "../layout/MobileHeader";

import { MobileContent } from "../layout/MobileContent";

import { CompanyAvatar } from "../companies/CompanyAvatar";

import { NoteCreate, NotesIterator, NotesIteratorMobile } from "../notes";

import { NoteCreateSheet } from "../notes/NoteCreateSheet";

import { TagsListEdit } from "./TagsListEdit";

import { ContactEditSheet } from "./ContactEditSheet";

import { ContactPersonalInfo } from "./ContactPersonalInfo";

import { ContactBackgroundInfo } from "./ContactBackgroundInfo";

import { ContactTasksList } from "./ContactTasksList";

import type { Contact } from "../types";

import { Avatar } from "./Avatar";

import { ContactAside } from "./ContactAside";

import { MobileBackButton } from "../misc/MobileBackButton";

export const ContactShow = (props: ShowBaseProps = {}) => {
  const isMobile = useIsMobile();

  return (
    <ShowBase
      queryOptions={{
        onError: isMobile
          ? () => {
              // Ошибка обрабатывается
              // мобильным интерфейсом.
            }
          : undefined,
      }}
      {...props}
    >
      {isMobile ? <ContactShowContentMobile /> : <ContactShowContent />}
    </ShowBase>
  );
};

const ContactShowContentMobile = () => {
  const translate = useTranslate();

  const { record, isPending, refetch } = useShowContext<Contact>();

  const [noteCreateOpen, setNoteCreateOpen] = useState(false);
  const [section, setSection] = useState("notes");

  const [editOpen, setEditOpen] = useState(false);

  if (isPending || !record) {
    return (
      <MobileRecordState
        loading={isPending}
        backTo="/contacts"
        title="Не удалось открыть контакт"
        onRetry={() => void refetch()}
      />
    );
  }

  const taskCount = record.nb_tasks ?? 0;

  return (
    <>
      <NoteCreateSheet
        open={noteCreateOpen}
        onOpenChange={setNoteCreateOpen}
        contact_id={record.id}
      />

      <ContactEditSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        contactId={record.id}
      />

      <MobileHeader
        actions={
          <CanAccess resource="contacts" action="edit">
            <Button
              variant="ghost"
              className="gap-2"
              aria-label="Редактировать контакт"
              onClick={() => setEditOpen(true)}
            >
              <Pencil className="size-4" /> Изменить
            </Button>
          </CanAccess>
        }
      >
        <MobileBackButton to="/contacts" />
      </MobileHeader>

      <MobileContent>
        <div data-mobile-reveal className="mobile-counterparty-hero mb-6">
          <Avatar />
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-semibold break-words">
              <RecordRepresentation />
            </h2>
            {record.title && (
              <p className="mt-1 text-sm text-muted-foreground">
                {record.title}
              </p>
            )}
            {record.company_id != null && (
              <ReferenceField
                source="company_id"
                reference="companies"
                link="show"
                className="mt-1 block text-sm text-muted-foreground"
              >
                <TextField source="name" className="underline" />
              </ReferenceField>
            )}
          </div>
        </div>

        <div className="w-full">
          <SegmentedControl
            label="Раздел карточки контакта"
            className="w-full"
            value={section}
            onValueChange={setSection}
            options={[
              { value: "notes", label: "Заметки" },
              { value: "tasks", label: `Задачи (${taskCount})` },
              { value: "details", label: "Контакт" },
            ]}
          />
          {section === "notes" && (
            <section
              data-mobile-tab-panel
              aria-label="Заметки контакта"
              className="mt-4"
            >
              <CanAccess resource="contact_notes" action="create">
                <Button
                  variant="outline"
                  className="mb-4"
                  onClick={() => setNoteCreateOpen(true)}
                >
                  Добавить заметку
                </Button>
              </CanAccess>
              <InfiniteListBase
                resource="contact_notes"
                filter={{
                  contact_id: record.id,
                }}
                sort={{
                  field: "date",
                  order: "DESC",
                }}
                perPage={25}
                disableSyncWithLocation
                storeKey={false}
                empty={
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <p className="text-muted-foreground mb-4">
                      {translate("resources.notes.empty")}
                    </p>
                  </div>
                }
                loading={false}
                error={false}
                queryOptions={{
                  onError: () => {
                    // Ошибка выводится
                    // внутри списка.
                  },
                }}
              >
                <NotesIteratorMobile contactId={record.id} showStatus />
              </InfiniteListBase>
            </section>
          )}

          {section === "tasks" && (
            <section
              data-mobile-tab-panel
              aria-label="Задачи контакта"
              className="mt-4"
            >
              <ContactTasksList />
            </section>
          )}

          {section === "details" && (
            <section
              data-mobile-tab-panel
              aria-label="Данные контакта"
              className="mt-4"
            >
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold">Контактные данные</h3>

                  <Separator />

                  <div className="mt-3">
                    <ContactPersonalInfo />
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold">Заметка</h3>

                  <Separator />

                  <div className="mt-3">
                    <ContactBackgroundInfo />
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold">Теги</h3>

                  <Separator />

                  <div className="mt-3">
                    <TagsListEdit />
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      </MobileContent>
    </>
  );
};

const ContactShowContent = () => {
  const translate = useTranslate();

  const { record, isPending } = useShowContext<Contact>();

  if (isPending || !record) {
    return null;
  }

  return (
    <div className="mt-2 mb-2 flex flex-col lg:flex-row gap-6 min-w-0">
      <div className="flex-1 min-w-0">
        <Card>
          <CardContent>
            <div className="flex">
              <Avatar />

              <div className="ml-2 flex-1 min-w-0">
                <h5 className="text-xl font-semibold">
                  <RecordRepresentation />
                </h5>

                {(record.title || record.company_id != null) && (
                  <div className="inline-flex text-sm text-muted-foreground">
                    {record.title && record.company_id != null
                      ? `${translate("resources.contacts.position_at", {
                          title: record.title,
                        })} `
                      : record.title}

                    {record.company_id != null && (
                      <ReferenceField
                        source="company_id"
                        reference="companies"
                        link="show"
                      >
                        &nbsp;
                        <TextField source="name" />
                      </ReferenceField>
                    )}
                  </div>
                )}
              </div>

              {record.company_id != null && (
                <ReferenceField
                  source="company_id"
                  reference="companies"
                  link="show"
                  className="no-underline"
                >
                  <CompanyAvatar />
                </ReferenceField>
              )}
            </div>

            <InfiniteListBase
              resource="contact_notes"
              filter={{
                contact_id: record.id,
              }}
              sort={{
                field: "date",
                order: "DESC",
              }}
              perPage={25}
              disableSyncWithLocation
              storeKey={false}
              empty={
                <CanAccess resource="contact_notes" action="create">
                  <NoteCreate
                    reference="contacts"
                    showStatus
                    className="mt-4"
                  />
                </CanAccess>
              }
            >
              <NotesIterator reference="contacts" showStatus />
            </InfiniteListBase>
          </CardContent>
        </Card>
      </div>

      <ContactAside />
    </div>
  );
};
